/**
 * Effective-tenant resolver -- the single source of truth for "which
 * distributor (tenant) is the app currently acting as?"
 *
 * The sidebar event bus (see eventBus.js) carries a ``tenantId`` in mutation
 * event payloads so a summary subscriber can ignore events for a tenant it is
 * not currently showing (design Section 7b, stale-tenant guard). Both the
 * emit site (clientsWorkspace.setClientStatus) and the guard
 * (distributorSummary.maybeRefetch) must derive "current tenant" the SAME way,
 * or the guard would drop legitimate events. This module is that one way.
 *
 * Two cases produce an effective tenant:
 *   - SuperAdmin impersonating a distributor: the impersonation store holds
 *     ``distributorId`` (impersonation.js). This takes precedence -- while
 *     impersonating, the true-SA account has no distributor of its own.
 *   - A real distributor account logged in: the account store holds
 *     ``distributor.{id}`` from the login payload (account.js).
 *
 * Returns ``null`` when neither applies (true SuperAdmin not impersonating,
 * or before hydrate). A null tenant means "no relevance filtering possible" --
 * per design 7b, a null on either side is treated as "always relevant" by the
 * guard (a coarse refetch is never a correctness bug; it is at worst a
 * redundant network call).
 *
 * Design: docs/design/2026-07-14-sidebar-event-driven-refresh-design.md
 * (Section 7b).
 */
import { useImpersonationStore } from '@/stores/impersonation'
import { useAccountStore } from '@/stores/account'

/**
 * Resolve the current effective tenant (distributor) id, or null.
 *
 * Reads live from the two identity stores each call rather than caching --
 * impersonation start/stop and login/logout mutate them, and the value is
 * only read at emit/guard time (not hot).
 */
export function effectiveTenantId() {
    const imp = useImpersonationStore()
    if (imp.active && imp.distributorId != null) {
        return imp.distributorId
    }
    const account = useAccountStore()
    return account.distributor?.id ?? null
}
